import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { BookOpen, Download, Rocket, RotateCcw, Volume2 } from "lucide-react";

const playGreeting = () => {
  try {
    const a = window.__lwbGreeting || (window.__lwbGreeting = new Audio("/audio/hello-explorer.mp3"));
    a.currentTime = 0;
    a.volume = 0.4;
    return a.play();
  } catch {
    return Promise.reject(new Error("audio unavailable"));
  }
};

const playVoice = (key, src) => {
  try {
    const store = window.__lwbVoices || (window.__lwbVoices = {});
    const a = store[key] || (store[key] = new Audio(src));
    a.currentTime = 0;
    a.volume = 0.4;
    return a.play();
  } catch {
    return Promise.reject(new Error("audio unavailable"));
  }
};

const playSound = (type) => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = window.__lwbAudio || (window.__lwbAudio = new Ctx());
    if (ctx.state === "suspended") ctx.resume();
    const notes = {
      pop: [[520, 0, 0.12, "triangle"]],
      boop: [[196, 0, 0.18, "sawtooth"], [155, 0.16, 0.22, "sawtooth"]],
      fanfare: [[523, 0, 0.16, "triangle"], [659, 0.16, 0.16, "triangle"], [784, 0.32, 0.16, "triangle"], [1047, 0.48, 0.4, "triangle"]],
    }[type];
    notes.forEach(([f, t, d, wave]) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = wave;
      o.frequency.value = f;
      g.gain.setValueAtTime(0.001, ctx.currentTime + t);
      g.gain.exponentialRampToValueAtTime(0.08, ctx.currentTime + t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + d);
      o.connect(g).connect(ctx.destination);
      o.start(ctx.currentTime + t);
      o.stop(ctx.currentTime + t + d + 0.05);
    });
  } catch {}
};

const CHALLENGES = [
  {
    intro: "Your first challenge…",
    question: "Can you find the famous clock tower?",
    options: [
      { id: "eye", name: "London Eye", img: "/images/landmark-eye.png", cover: true },
      { id: "bigben", name: "Big Ben", img: "/images/landmark-bigben.png", cover: true, correct: true },
      { id: "palace", name: "Buckingham Palace", img: "/images/landmark-palace.png", cover: true },
    ],
  },
  {
    intro: "Challenge two…",
    question: "Can you spot the big red bus?",
    options: [
      { id: "phonebox", name: "Telephone Box", img: "/images/explorer-phonebox.png" },
      { id: "bus", name: "Red Bus", img: "/images/explorer-bus.png", correct: true },
      { id: "postbox", name: "Post Box", img: "/images/explorer-postbox.png" },
    ],
  },
  {
    intro: "Final challenge…",
    question: "Can you find the palace guard?",
    options: [
      { id: "postbox3", name: "Post Box", img: "/images/explorer-postbox.png" },
      { id: "guard", name: "Palace Guard", img: "/images/explorer-guard.png", correct: true },
      { id: "phonebox3", name: "Telephone Box", img: "/images/explorer-phonebox.png" },
    ],
  },
];

const BURST = [
  { x: -140, y: -90, c: "#FFE9A8" }, { x: 140, y: -100, c: "#A8D5A2" }, { x: -180, y: 20, c: "#BFE0F7" },
  { x: 180, y: 30, c: "#F5B8A6" }, { x: -80, y: -160, c: "#F5B8A6" }, { x: 90, y: -170, c: "#FFE9A8" },
  { x: -60, y: 120, c: "#A8D5A2" }, { x: 70, y: 130, c: "#BFE0F7" }, { x: 0, y: -190, c: "#4FA3E3" },
  { x: -190, y: -40, c: "#FFE9A8" }, { x: 190, y: -50, c: "#A8D5A2" }, { x: 0, y: 170, c: "#F5B8A6" },
];

const BADGE_IMG = "/images/badge-explorer.png";

const FIREWORKS = [
  { x: "10%", y: "16%", c: "#F5A623", delay: 0 },
  { x: "88%", y: "12%", c: "#4FA3E3", delay: 0.5 },
  { x: "76%", y: "42%", c: "#71BE6B", delay: 1.0 },
  { x: "18%", y: "52%", c: "#E8564A", delay: 1.4 },
  { x: "50%", y: "8%", c: "#C78A0F", delay: 0.8 },
];

const FireworkBurst = ({ x, y, c, delay }) => (
  <div className="firework" style={{ left: x, top: y }}>
    <span className="firework-flash" style={{ animationDelay: `${delay}s` }} />
    {Array.from({ length: 12 }).map((_, i) => {
      const a = (i / 12) * Math.PI * 2;
      const d = 70 + (i % 3) * 26;
      return (
        <span
          key={i}
          className="firework-dot"
          style={{
            background: i % 4 === 3 ? "#FFFDF2" : c,
            animationDelay: `${delay}s`,
            "--dx": `${Math.cos(a) * d}px`,
            "--dy": `${Math.sin(a) * d}px`,
          }}
        />
      );
    })}
  </div>
);

const Streamer = ({ color, style, delay = 0 }) => (
  <svg className="streamer" style={{ ...style, animationDelay: `${delay}s` }} width="26" height="120" viewBox="0 0 26 120">
    <path
      d="M13 0 C 24 14 2 24 13 38 C 24 52 2 62 13 76 C 24 90 2 100 13 116"
      fill="none"
      stroke={color}
      strokeWidth="6"
      strokeLinecap="round"
    />
  </svg>
);

export default function LondonExplorer() {
  const [stage, setStage] = useState("welcome");
  const [challengeIdx, setChallengeIdx] = useState(0);
  const [shakeId, setShakeId] = useState(null);
  const [oops, setOops] = useState(false);
  const [badges, setBadges] = useState(() =>
    Number(localStorage.getItem("lwb-badges") || (localStorage.getItem("lwb-badge-1") === "yes" ? 1 : 0))
  );
  const [showListenChip, setShowListenChip] = useState(false);

  useEffect(() => {
    if (stage === "welcome") {
      playGreeting()
        .then(() => setShowListenChip(false))
        .catch(() => setShowListenChip(true));
    } else if (stage === "badge") {
      playVoice("found", "/audio/you-found-it.mp3").catch(() => {});
    } else if (stage === "complete") {
      playVoice("yay", "/audio/yay.mp3").catch(() => {});
    }
  }, [stage]);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  const pick = (lm) => {
    if (lm.correct) {
      playSound("fanfare");
      const n = Math.max(badges, challengeIdx + 1);
      localStorage.setItem("lwb-badges", String(n));
      setBadges(n);
      setStage("badge");
    } else {
      playSound("boop");
      setOops(true);
      setShakeId(lm.id + Date.now());
      setTimeout(() => setOops(false), 2400);
    }
  };

  const nextChallenge = () => {
    playSound("pop");
    setChallengeIdx((i) => i + 1);
    setStage("challenge");
  };

  const playAgain = () => {
    playSound("pop");
    setChallengeIdx(0);
    setStage("welcome");
  };

  return (
    <div
      data-testid="london-explorer"
      className="min-h-screen relative overflow-hidden flex flex-col"
      style={{ background: "linear-gradient(180deg, #BFE0F7 0%, #EAF3FB 55%, #FFFDF2 100%)" }}
    >
      <svg className="cloud cloud-a" viewBox="0 0 80 40" width="90" height="45">
        <ellipse cx="28" cy="28" rx="22" ry="11" fill="#FFFFFF" />
        <ellipse cx="48" cy="22" rx="18" ry="12" fill="#FFFFFF" />
      </svg>
      <svg className="cloud cloud-b" viewBox="0 0 80 40" width="64" height="32">
        <ellipse cx="26" cy="28" rx="20" ry="10" fill="#FFFFFF" />
        <ellipse cx="44" cy="22" rx="16" ry="11" fill="#FFFFFF" />
      </svg>

      <header className="relative z-20 flex items-center justify-between px-5 sm:px-8 py-4">
        <Link to="/" data-testid="explorer-home-link" className="flex items-center gap-2 opacity-80 hover:opacity-100 transition-opacity">
          <img src="/images/lwb-logo.png" alt="Learn With Bailey" className="h-9 w-auto" />
        </Link>
        {badges > 0 && (
          <span data-testid="badge-earned-chip" className="rounded-full bg-amberbrand text-cream text-xs font-bold uppercase tracking-widest px-4 py-2 shadow">
            Badges {badges}/3
          </span>
        )}
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center px-4 sm:px-6 pb-16">
        <AnimatePresence mode="wait">
          {stage === "welcome" && (
            <motion.div
              key="welcome"
              data-testid="explorer-welcome"
              className="text-center max-w-2xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, y: -30 }}
            >
              <motion.img
                src="/images/explorer-bailey.png"
                alt="Bailey the explorer"
                data-testid="bailey-character"
                className="w-40 sm:w-52 mx-auto mb-2 drop-shadow-[0_18px_24px_rgba(49,67,92,0.35)]"
                initial={{ y: 120, opacity: 0, scale: 0.6 }}
                animate={{ y: 0, opacity: 1, scale: 1 }}
                transition={{ type: "spring", stiffness: 120, damping: 11, delay: 0.2 }}
              />
              <motion.h1
                className="font-display font-extrabold tracking-tight text-4xl sm:text-6xl text-walnut"
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.45, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              >
                Hello Explorer!
              </motion.h1>
              <motion.div
                className="mt-5 bg-white/85 border-2 border-sand rounded-3xl px-7 py-5 shadow-lg inline-block text-left"
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.7, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              >
                <p className="font-display font-bold text-xl sm:text-2xl text-walnut">I'm Bailey!</p>
                <p className="text-base sm:text-lg text-walnutsoft mt-1">I'm exploring London today.</p>
                <p className="text-base sm:text-lg text-walnutsoft">Are you ready to come with me?</p>
              </motion.div>
              {showListenChip && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.15 }}
                >
                  <motion.button
                    data-testid="listen-bailey-button"
                    onClick={() => playGreeting().then(() => setShowListenChip(false)).catch(() => {})}
                    className="mt-4 rounded-full bg-white/85 border-2 border-sand px-6 py-2.5 font-display font-bold text-sm sm:text-base text-walnut inline-flex items-center gap-2 shadow-md"
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                    whileHover={{ scale: 1.08 }}
                    whileTap={{ scale: 0.94 }}
                  >
                    <Volume2 size={17} /> Tap to hear Bailey say hello
                  </motion.button>
                </motion.div>
              )}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 160, damping: 10, delay: 1.05 }}
              >
                <motion.button
                  data-testid="lets-go-button"
                  onClick={() => { playSound("pop"); setStage("challenge"); }}
                  className="mt-8 rounded-full bg-terracotta text-cream font-display font-extrabold text-xl sm:text-2xl px-12 py-5 inline-flex items-center gap-3 shadow-[0_20px_40px_-12px_rgba(79,163,227,0.8)]"
                  animate={{ scale: [1, 1.06, 1] }}
                  transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
                  whileHover={{ scale: 1.12 }}
                  whileTap={{ scale: 0.94 }}
                >
                  LET'S GO! <Rocket size={26} />
                </motion.button>
              </motion.div>
            </motion.div>
          )}

          {stage === "challenge" && (
            <motion.div
              key={`challenge-${challengeIdx}`}
              data-testid="explorer-challenge"
              className="text-center max-w-4xl w-full"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
            >
              <p className="font-mono text-sm sm:text-base uppercase tracking-[0.25em] text-amberbrand mb-3">
                {CHALLENGES[challengeIdx].intro}
              </p>
              <div data-testid="challenge-progress" className="flex items-center justify-center gap-2.5 mb-6">
                {CHALLENGES.map((_, i) => (
                  <span
                    key={i}
                    className={`h-3 rounded-full transition-all duration-500 ${
                      i < challengeIdx ? "w-8 bg-sage" : i === challengeIdx ? "w-8 bg-terracotta" : "w-3 bg-walnut/20"
                    }`}
                  />
                ))}
              </div>
              <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-5xl text-walnut mb-10">
                {CHALLENGES[challengeIdx].question}
              </h2>
              <div className="grid grid-cols-3 gap-3 sm:gap-8 max-w-3xl mx-auto">
                {CHALLENGES[challengeIdx].options.map((lm, i) => (
                  <motion.button
                    key={lm.id}
                    data-testid={`challenge-${lm.id}`}
                    onClick={() => pick(lm)}
                    className="group bg-white/85 border-2 border-sand rounded-3xl p-4 sm:p-7 shadow-lg cursor-pointer"
                    initial={{ y: 60, opacity: 0 }}
                    animate={
                      shakeId && shakeId.startsWith(lm.id)
                        ? { x: [0, -12, 12, -9, 9, 0], y: 0, opacity: 1 }
                        : { y: 0, opacity: 1, x: 0 }
                    }
                    transition={{ delay: i * 0.15, type: "spring", stiffness: 140, damping: 12 }}
                    whileHover={{ scale: 1.07, rotate: -1.5 }}
                    whileTap={{ scale: 0.93 }}
                  >
                    <img
                      src={lm.img}
                      alt={lm.name}
                      className={`w-full h-32 sm:h-48 rounded-2xl ${lm.cover ? "object-cover" : "object-contain"}`}
                    />
                    <span className="block mt-2 sm:mt-4 font-display font-bold text-xs sm:text-base text-walnut">{lm.name}</span>
                  </motion.button>
                ))}
              </div>
              <AnimatePresence>
                {oops && (
                  <motion.p
                    data-testid="try-again-message"
                    className="mt-8 inline-block bg-white/85 border-2 border-terracotta/40 rounded-full px-6 py-3 font-display font-bold text-walnut"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                  >
                    Not that one — try again, explorer!
                  </motion.p>
                )}
              </AnimatePresence>
            </motion.div>
          )}

          {stage === "badge" && (
            <motion.div
              key="badge"
              data-testid="badge-screen"
              className="text-center max-w-2xl relative"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div data-testid="fireworks-layer" className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
                {FIREWORKS.map((f, i) => (
                  <FireworkBurst key={i} {...f} />
                ))}
                <Streamer color="#E8564A" style={{ left: "6%" }} delay={0} />
                <Streamer color="#4FA3E3" style={{ left: "12%" }} delay={0.7} />
                <Streamer color="#71BE6B" style={{ right: "9%" }} delay={0.4} />
                <Streamer color="#F5A623" style={{ right: "4%" }} delay={1.1} />
                <Streamer color="#F5B8A6" style={{ left: "48%" }} delay={0.9} />
              </div>
              {BURST.map((s, i) => (
                <motion.span
                  key={i}
                  className="absolute left-1/2 top-24 text-2xl pointer-events-none"
                  style={{ color: s.c }}
                  initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
                  animate={{ x: s.x, y: s.y, scale: [0, 1.4, 1], opacity: [1, 1, 0] }}
                  transition={{ duration: 1.4, delay: 0.1 + i * 0.04, ease: "easeOut" }}
                >
                  ✦
                </motion.span>
              ))}
              <motion.h2
                className="font-display font-extrabold tracking-tight text-4xl sm:text-6xl text-walnut"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 150, damping: 10 }}
              >
                YOU FOUND IT!
              </motion.h2>
              <motion.div
                className="my-6 inline-block"
                initial={{ scale: 0, rotate: -30 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 120, damping: 8, delay: 0.35 }}
              >
                <img src={BADGE_IMG} alt="London Explorer badge" data-testid="explorer-badge-image" className="w-44 sm:w-56 drop-shadow-[0_20px_30px_rgba(49,67,92,0.35)]" />
              </motion.div>
              <motion.p
                className="font-display font-bold text-xl sm:text-2xl text-walnut"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                {challengeIdx === 0
                  ? "You've earned your first Bailey London Badge!"
                  : `Bailey London Badge No. ${challengeIdx + 1} is yours!`}
              </motion.p>
              <motion.div
                className="mt-8 flex flex-wrap justify-center gap-4"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.85 }}
              >
                {challengeIdx < CHALLENGES.length - 1 ? (
                  <button
                    data-testid="next-challenge-button"
                    onClick={nextChallenge}
                    className="rounded-full bg-terracotta text-cream font-display font-extrabold text-lg sm:text-xl px-10 py-4 inline-flex items-center gap-2 hover:bg-terracottadark transition-colors shadow-[0_16px_32px_-12px_rgba(79,163,227,0.7)]"
                  >
                    Next challenge <Rocket size={20} />
                  </button>
                ) : (
                  <button
                    data-testid="finish-adventure-button"
                    onClick={() => { playSound("fanfare"); setStage("complete"); }}
                    className="rounded-full bg-terracotta text-cream font-display font-extrabold text-lg sm:text-xl px-10 py-4 inline-flex items-center gap-2 hover:bg-terracottadark transition-colors shadow-[0_16px_32px_-12px_rgba(79,163,227,0.7)]"
                  >
                    Finish the adventure <Rocket size={20} />
                  </button>
                )}
              </motion.div>
            </motion.div>
          )}

          {stage === "complete" && (
            <motion.div
              key="complete"
              data-testid="complete-screen"
              className="text-center max-w-2xl relative"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
                {FIREWORKS.map((f, i) => (
                  <FireworkBurst key={i} {...f} />
                ))}
                <Streamer color="#E8564A" style={{ left: "6%" }} delay={0} />
                <Streamer color="#4FA3E3" style={{ left: "12%" }} delay={0.7} />
                <Streamer color="#71BE6B" style={{ right: "9%" }} delay={0.4} />
                <Streamer color="#F5A623" style={{ right: "4%" }} delay={1.1} />
                <Streamer color="#F5B8A6" style={{ left: "48%" }} delay={0.9} />
              </div>
              <motion.h2
                className="font-display font-extrabold tracking-tight text-4xl sm:text-6xl text-walnut"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 150, damping: 10 }}
              >
                LONDON EXPLORER!
              </motion.h2>
              <motion.p
                className="mt-4 font-display font-bold text-xl sm:text-2xl text-walnut"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                You've earned all three Bailey London Badges — London is yours!
              </motion.p>
              <div className="my-8 flex items-center justify-center gap-2 sm:gap-6">
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    data-testid={`complete-badge-${i + 1}`}
                    initial={{ scale: 0, rotate: -25, y: 30 }}
                    animate={{ scale: 1, rotate: i === 1 ? 0 : i === 0 ? -8 : 8, y: 0 }}
                    transition={{ type: "spring", stiffness: 130, damping: 9, delay: 0.55 + i * 0.18 }}
                    className="relative"
                  >
                    <img src={BADGE_IMG} alt={`Bailey London Badge No. ${i + 1}`} className="w-24 sm:w-36 drop-shadow-[0_16px_24px_rgba(49,67,92,0.35)]" />
                    <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-walnut text-cream text-[10px] sm:text-xs font-bold rounded-full px-3 py-1">
                      No. {i + 1}
                    </span>
                  </motion.div>
                ))}
              </div>
              <motion.div
                className="mt-10 flex flex-wrap justify-center gap-4"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2 }}
              >
                <button
                  data-testid="play-again-button"
                  onClick={playAgain}
                  className="rounded-full border-2 border-walnut text-walnut font-display font-bold text-base px-8 py-4 inline-flex items-center gap-2 hover:bg-walnut hover:text-cream transition-colors"
                >
                  <RotateCcw size={18} /> Play again
                </button>
                <a
                  href="/downloads/london-explorer-gift.pdf"
                  download="Bailey-Explorer-in-London.pdf"
                  data-testid="explorer-download-gift"
                  className="rounded-full bg-amberbrand text-cream font-display font-bold text-base px-8 py-4 inline-flex items-center gap-2 hover:brightness-95 transition shadow-[0_16px_32px_-12px_rgba(199,138,15,0.6)]"
                >
                  <Download size={18} /> Download my Exploring in London gift
                </a>
                <Link
                  to="/books/bailey-in-london"
                  data-testid="get-book-button"
                  className="rounded-full bg-terracotta text-cream font-display font-bold text-base px-8 py-4 inline-flex items-center gap-2 hover:bg-terracottadark transition-colors shadow-[0_16px_32px_-12px_rgba(79,163,227,0.7)]"
                >
                  <BookOpen size={18} /> Get the Bailey in London book
                </Link>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      <div className="relative z-0 h-10 bg-sagecream border-t-4 border-dashed border-sand" />
    </div>
  );
}
